import React, { useState } from "react";
import { Box, TextField, Typography, Button, IconButton, Tooltip, Alert } from "@mui/material";
import {
  AutoFixHigh as FormatIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Code as CodeIcon,
  CleaningServices as ClearIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";

interface JsonEditorInputProps {
  value: string;
  onChange: (val: string) => void;
  label?: string;
  placeholder?: string;
  minRows?: number;
  maxRows?: number;
  readOnly?: boolean;
}

export const JsonEditorInput: React.FC<JsonEditorInputProps> = ({
  value,
  onChange,
  label = "Payload JSON",
  placeholder = '{\n  "key": "value"\n}',
  minRows = 4,
  maxRows = 12,
  readOnly = false,
}) => {
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const validateJson = (text: string) => {
    if (!text || !text.trim()) {
      setJsonError(null);
      return true;
    }
    try {
      JSON.parse(text);
      setJsonError(null);
      return true;
    } catch (err: unknown) {
      if (err instanceof Error) {
        setJsonError(err.message);
      } else {
        setJsonError("Invalid JSON syntax");
      }
      return false;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    onChange(val);
    validateJson(val);
  };

  const handleFormat = () => {
    if (!value || !value.trim()) return;
    try {
      const parsed = JSON.parse(value);
      const formatted = JSON.stringify(parsed, null, 2);
      onChange(formatted);
      setJsonError(null);
      toast.success("JSON formatted successfully");
    } catch {
      toast.error("Cannot format invalid JSON syntax");
    }
  };

  const handleCopy = () => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleClear = () => {
    onChange("");
    setJsonError(null);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CodeIcon sx={{ fontSize: 18, color: "primary.main" }} />
          <Typography variant="subtitle2" fontWeight="700" color="text.primary">
            {label}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          {!readOnly && (
            <Button
              size="small"
              startIcon={<FormatIcon sx={{ fontSize: 16 }} />}
              onClick={handleFormat}
              disabled={!value.trim()}
              sx={{
                fontSize: "0.75rem",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
              }}
            >
              Format JSON
            </Button>
          )}
          {value && (
            <Tooltip title="Copy JSON">
              <IconButton size="small" onClick={handleCopy}>
                {copied ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          )}
          {!readOnly && value && (
            <Tooltip title="Clear JSON">
              <IconButton size="small" onClick={handleClear} color="error">
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <TextField
        fullWidth
        multiline
        minRows={minRows}
        maxRows={maxRows}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        error={Boolean(jsonError)}
        slotProps={{
          input: {
            readOnly: readOnly,
            sx: {
              fontFamily: "'Fira Code', 'Consolas', 'Roboto Mono', monospace",
              fontSize: "0.85rem",
              backgroundColor: readOnly ? "action.hover" : "background.paper",
              borderRadius: "12px",
              lineHeight: 1.5,
            },
          },
        }}
      />

      {jsonError && (
        <Alert severity="error" sx={{ mt: 1, py: 0, borderRadius: "8px", fontSize: "0.78rem" }}>
          JSON Error: {jsonError}
        </Alert>
      )}
    </Box>
  );
};
